/* -----------------Globals--------------- */
import { useLocalSearchParams } from "expo-router";

import { cacheKeys } from "@constants/cache-keys";
import { events } from "@constants/tracking";
import { pageViewTypes } from "@constants/tracking";
import useAnalytics from "@hooks/useAnalytics";
import { useProductDetails } from "@hooks/useProduct";
import { calculatePlanExpiry, getDurationText } from "@memorang/helpers/bundle";
import { isWeb } from "@memorang/helpers/platform";
import type { ProductType } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import { PurchaseSuccessSkeleton } from "@memorang/ui/components/PurchaseSuccessApplet/PurchaseSuccessSkeleton";
import ScrollContainer from "@memorang/ui/components/ScrollContainer";
/* -----------------Child components--------------- */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchExamDetails } from "api/commerce/exam-service";
import { AccessGrantedView } from "components/AccessGrantedView";
import DocumentTitle from "components/DocumentTitle";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
const SuccessRoute = () => {
	const { productType } = useLocalSearchParams<{
		productType: ProductType;
	}>();

	const {
		duration,
		interval,
		amount,
		currency,
		planId,
		id: productId,
	} = useLocalSearchParams<{
		duration: string;
		interval: string;
		amount: string;
		currency: string;
		planId: string;
		id: string;
	}>();

	const { data: productDetails } = useProductDetails(
		productType,
		Number(productId),
	);

	const examId = productDetails?.examUUID;

	const { data: examDetails, isLoading: isLoadingExamDetails } = useQuery({
		queryKey: [cacheKeys.examDetails, examId],
		queryFn: async () => {
			if (!examId) {
				return undefined;
			}
			return await fetchExamDetails({ examId });
		},
		enabled: !!examId,
	});

	const { explicitCallTrackCustomEvent } = useAnalytics({
		type: pageViewTypes.purchaseSuccess,
	});

	const queryClient = useQueryClient();
	const { t } = useTranslation();

	const hasTrackedPurchaseRef = useRef(false);

	useEffect(() => {
		if (productDetails && !hasTrackedPurchaseRef.current) {
			hasTrackedPurchaseRef.current = true;
			const bundleType = productDetails.bundleType;
			explicitCallTrackCustomEvent({
				eventName: events.paymentSuccessful,
				product_id: productDetails.numericId,
				product_name: productDetails.name,
				plan_amount: amount,
				plan_currency: currency,
				plan_id: planId,
				product_type: bundleType,
			});
			queryClient.invalidateQueries();
		}
	}, [
		productDetails,
		queryClient,
		explicitCallTrackCustomEvent,
		amount,
		currency,
		planId,
	]);

	if (!productDetails || isLoadingExamDetails) {
		return (
			<>
				<DocumentTitle title={t("purchase.success.documentTitle")} />
				<ScrollContainer maxWidth="xs">
					<Box paddingTop={24} paddingBottom={44} paddingHorizontal={16}>
						<PurchaseSuccessSkeleton
							products={
								productDetails
									? [{ numFeatures: productDetails.features?.length ?? 0 }]
									: undefined
							}
							numActions={isWeb ? 2 : 1}
						/>
					</Box>
				</ScrollContainer>
			</>
		);
	}

	const planExpiry = duration
		? calculatePlanExpiry(interval, Number(duration))
		: undefined;
	const expirationDateText = duration
		? getDurationText(Number(duration), interval)
		: undefined;

	const {
		name,
		examName,
		logicalId: id,
		icon: { url },
		features,
		bundleType,
		bundleSubType,
	} = productDetails;

	const title = t("purchase.success.accessGranted", { name });

	const isPracticeTest =
		bundleType === "PRACTICE_TEST" || bundleSubType === "PRACTICE_TEST";

	const getDescription = () => {
		if (bundleType === "STUDY_PACK" || bundleSubType === "STUDY_PACK") {
			return t("purchase.success.studyPack");
		}
		if (isPracticeTest) {
			return t("purchase.success.practiceTest");
		}

		if (bundleType === "EXAM" || bundleSubType === "EXAM") {
			return t("purchase.success.combo");
		}
	};

	const products = [
		{
			id,
			title: name,
			description: getDescription(),
			icon: url,
			features,
			isCustomAccess: false,
			bundleType: isPracticeTest ? ("PRACTICE_TEST" as const) : bundleType,
		},
	];
	const successfulPurchaseTitle =
		expirationDateText && planExpiry
			? t("purchase.success.unlockedDuration", {
					duration: expirationDateText,
					name,
					expiry: planExpiry,
				})
			: undefined;

	return (
		<>
			<DocumentTitle title={t("purchase.success.documentTitle")} />
			<ScrollContainer maxWidth="xs">
				<Box paddingTop={24} paddingBottom={44} paddingHorizontal={16}>
					<AccessGrantedView
						products={products}
						examName={examName}
						title={title}
						examDetails={examDetails}
						successfulPurchaseTitle={successfulPurchaseTitle}
					/>
				</Box>
			</ScrollContainer>
		</>
	);
};

export default SuccessRoute;
